import { captureMessage } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useMedia } from "tamagui";

import { VerificationStatus } from "@/api/generated/models";
import { getGetUserProfileQueryKey, useGetUserProfile } from "@/api/queries/";
import { LayoutContext, YStack } from "@/components";
import { usePersonaEmbeddedInquiry } from "@/hooks/usePersonaEmbeddedInquiry";
import PendingContent from "./PendingContent";

// Persona recommends at least 650px for the inline frame, plus room for the dialog header.
const PERSONA_INLINE_MODAL_HEIGHT = 736;

const PERSONA_IFRAME_STYLE = {
  width: "100%",
  height: "100%",
  border: "none",
};

function PersonaInlineFrame({
  iframeRef,
  isReady,
  loadingIndicator,
}: {
  iframeRef: (iframe: HTMLIFrameElement | null) => void;
  isReady: boolean;
  loadingIndicator?: ReactNode;
}) {
  const { setModalOptions } = useContext(LayoutContext);

  useEffect(() => {
    setModalOptions((options) => ({
      ...options,
      height: PERSONA_INLINE_MODAL_HEIGHT,
    }));

    return () => {
      setModalOptions(({ height: _height, ...options }) => options);
    };
  }, [setModalOptions]);

  return (
    <YStack flex={1} position="relative">
      <iframe
        ref={iframeRef}
        title="Verify your identity"
        style={PERSONA_IFRAME_STYLE}
      />
      {!isReady && (
        <YStack
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          backgroundColor="$backgroundGround"
        >
          {loadingIndicator ?? <PendingContent />}
        </YStack>
      )}
    </YStack>
  );
}

function useShouldRenderPersonaInline(canRenderPersonaInline: boolean) {
  const { lg } = useMedia();
  return canRenderPersonaInline && lg;
}

export default function ConfirmIdVerification({
  onContinue,
  onCancelFallback,
  loadingIndicator,
  canRenderPersonaInline = true,
}: {
  onContinue: () => void;
  loadingIndicator?: ReactNode;
  onCancelFallback?: () => void;
  // Inline only fits a full-size host like the desktop progression modal; small hosts keep the overlay.
  canRenderPersonaInline?: boolean;
}) {
  const [canTryAgain, setCanTryAgain] = useState(false);
  const shouldRenderPersonaInline = useShouldRenderPersonaInline(
    canRenderPersonaInline,
  );
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useGetUserProfile({
    refetchOnWindowFocus: true,
  });

  const {
    start: startPersona,
    isInlineOpen,
    isInlineReady,
    inlineIframeRef,
  } = usePersonaEmbeddedInquiry({
    shouldRenderInline: shouldRenderPersonaInline,
    onComplete: () => {
      setCanTryAgain(false);
      queryClient.invalidateQueries({
        queryKey: getGetUserProfileQueryKey(),
        exact: false,
      });
    },
    onCancel: () => {
      if (onCancelFallback) {
        onCancelFallback();
        return;
      }

      setCanTryAgain(true);
    },
    onPersonaError: () => setCanTryAgain(true),
    onResumeError: () => setCanTryAgain(true),
  });

  const startedPersonaRef = useRef(false);
  useEffect(() => {
    const status = data?.idVerification?.status;

    if (
      status === VerificationStatus.pending_fallback &&
      !startedPersonaRef.current
    ) {
      startedPersonaRef.current = true;
      startPersona();
    }

    if (status !== VerificationStatus.pending_fallback) {
      startedPersonaRef.current = false;
    }
  }, [data?.idVerification?.status, startPersona]);

  const hasAdvancedRef = useRef(false);
  useEffect(() => {
    if (
      data?.idVerification?.status === VerificationStatus.approved &&
      !hasAdvancedRef.current
    ) {
      hasAdvancedRef.current = true;
      onContinue();
    }
  }, [data?.idVerification?.status, onContinue]);

  const status = data?.idVerification?.status;

  if (isLoading || (!status && isFetching)) {
    return loadingIndicator ?? <PendingContent />;
  }

  if (status === VerificationStatus.approved) {
    return loadingIndicator ?? <PendingContent />;
  }

  if (status === VerificationStatus.pending) {
    return loadingIndicator ?? <PendingContent showSupportLink />;
  }

  if (isInlineOpen) {
    return (
      <PersonaInlineFrame
        iframeRef={inlineIframeRef}
        isReady={isInlineReady}
        loadingIndicator={loadingIndicator}
      />
    );
  }

  if (status === VerificationStatus.pending_fallback) {
    return canTryAgain ? (
      <PendingContent
        message={{
          title: "Please verify your identity",
          subtitle:
            "In order to continue and see a clinician, we need to verify your identity. Please try again or contact support.",
        }}
        ctaLabel="Try again"
        onCtaPress={() => {
          startedPersonaRef.current = false;
          startPersona();
        }}
        showSupportLink
      />
    ) : (
      (loadingIndicator ?? <PendingContent showSupportLink />)
    );
  }

  if (status) {
    captureMessage(`ID Verification Error - Status: ${status}`, {
      extra: {
        context: "IdVerificationStep",
        userProfileQuery: { isLoading, isFetching },
      },
    });
    return (
      <PendingContent
        message={{
          title: "We couldn't verify your identity",
          subtitle: "Please contact support.",
        }}
        ctaLabel="Contact support"
        onCtaPress={() =>
          window.open("https://www.curaihealth.com/support", "_blank")
        }
      />
    );
  }

  return loadingIndicator ?? <PendingContent />;
}
